// @mui
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import * as Yup from 'yup';
// form
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
// components
import { LoadingButton } from '@mui/lab';
import { useEffect } from 'react';
import Editor from 'src/components/editor';
import FormProvider, {
  RHFAutocomplete,
  RHFCheckbox,
  RHFTextField,
} from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { CreateTemplateDtoTemplateTypeEnum } from 'woofwoof-api';
import {
  categoryTranslations,
  templateTypeTranslations,
} from '../context/TemplatesContext';
import { useTemplatesContext } from '../hooks/useTemplatesContext';
import TemplatesDeleteModal from './TemplatesDeleteModal';
import { translateKey } from 'src/locales/translate';


// ----------------------------------------------------------------------

export type TemplatesFormModalValuesProps = {
  name: string;
  description: string;
  category: string;
  templateType: CreateTemplateDtoTemplateTypeEnum;
  content: string;
  isShared: boolean;
};

export default function TemplatesFormModal() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDown450Px = useMediaQuery((theme) => theme.breakpoints.down(450));
  const {
    isModalOpen,
    handleCloseModal,
    isCreateTemplateLoading,
    templateCategories,
    isTemplateCategoriesLoading,
    handleCreateTemplate,
    handleUpdateTemplate,
    selectedTemplateId,
    templatesData,
    isUpdateTemplateLoading,
    handleOpenDeleteModal,
    isDeleteTemplateLoading,
  } = useTemplatesContext();
  const isEditing = !!selectedTemplateId;
  const template = templatesData?.find(
    (template) => template.id === selectedTemplateId,
  );

  const categoriesOptions = templateCategories
    .map((cat) => ({
      value: cat,
      label:
        categoryTranslations[cat] ||
        cat
          .toString()
          .split('_')
          .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' '),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const templateTypeOptions = Object.values(
    CreateTemplateDtoTemplateTypeEnum,
  ).map((type) => ({
    value: type,
    label:
      templateTypeTranslations[type] ||
      type
        .toString()
        .split('_')
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
  }));

  const TemplateSchema: Yup.Schema<TemplatesFormModalValuesProps> =
    Yup.object().shape({
      name: Yup.string().required('Nazwa jest wymagana'),
      description: Yup.string().required('Opis jest wymagany'),
      category: Yup.string().required('Kategoria jest wymagana'),
      templateType: Yup.string()
        .oneOf(
          Object.values(CreateTemplateDtoTemplateTypeEnum),
          translateKey("hardcoded.nieprawidlowy_typ_szablonu"),
        )
        .required('Typ jest wymagany'),
      content: Yup.string().required(translateKey("hardcoded.tresc_jest_wymagana")),
      isShared: Yup.boolean().required(),
    });

  const defaultValues: TemplatesFormModalValuesProps = {
    name: template?.name || '',
    description: template?.description || '',
    category: template?.category || '',
    templateType:
      template?.templateType || CreateTemplateDtoTemplateTypeEnum.General,
    content: template?.content || '',
    isShared: template?.isShared || false,
  };

  const methods = useForm<TemplatesFormModalValuesProps>({
    resolver: yupResolver(TemplateSchema),
    defaultValues,
    values: defaultValues,
  });

  const { setValue, handleSubmit, reset, getValues, watch } = methods;
  const formValues = getValues();
  const categoryValue = watch('category');
  const templateTypeValue = watch('templateType');

  const selectedCategoryOption = categoriesOptions?.find(
    (option: any) => option.value === categoryValue,
  );

  const selectedTemplateTypeOption = templateTypeOptions?.find(
    (option: any) => option.value === templateTypeValue,
  );

  useEffect(() => {
    if (!isModalOpen) {
      reset();
    }
  }, [isModalOpen, reset]);

  return (
    <>
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="tabletSm"
        sx={{
          '& .MuiDialog-paperScrollBody': {
            maxWidth: isDown450Px ? 'calc(100% - 40px) !important' : undefined,
          },
        }}
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
        >
          {isEditing ? 'Edytuj szablon' : 'Dodaj nowy szablon'}
          <Box flex={1} />
          <IconButton aria-label="close" onClick={handleCloseModal}>
            <Iconify icon="eva:close-fill" width={24} height={24} />
          </IconButton>
        </DialogTitle>
        <FormProvider
          methods={methods}
          onSubmit={handleSubmit(
            isEditing ? handleUpdateTemplate : handleCreateTemplate,
          )}
        >
          <DialogContent>
            <Stack sx={{ pt: 2, pb: 2 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: { xs: 2, md: 2 },
                }}
              >
                <Box sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}>
                  <RHFTextField
                    required
                    name="name"
                    label="Nazwa"
                    disabled={
                      isCreateTemplateLoading ||
                      isTemplateCategoriesLoading ||
                      isUpdateTemplateLoading ||
                      isDeleteTemplateLoading
                    }
                  />
                </Box>

                <Box sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}>
                  <RHFTextField
                    required
                    name="description"
                    label={translateKey("search.ui.aboutTab")}
                    disabled={
                      isCreateTemplateLoading ||
                      isTemplateCategoriesLoading ||
                      isUpdateTemplateLoading ||
                      isDeleteTemplateLoading
                    }
                  />
                </Box>

                <RHFAutocomplete
                  name="category"
                  label={translateKey("search.ui.category")}
                  disabled={
                    isCreateTemplateLoading ||
                    isTemplateCategoriesLoading ||
                    isUpdateTemplateLoading ||
                    isDeleteTemplateLoading
                  }
                  freeSolo
                  options={categoriesOptions || []}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      const formattedValue = newValue.value
                        .trim()
                        .toLowerCase()
                        .replace(/\s+/g, '_');

                      return formattedValue;
                    } else {
                      setValue('category', '');

                      return '';
                    }
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    // Handle when user types and blurs without selecting
                    if (reason === 'input' && newInputValue) {
                      const formattedValue = newInputValue
                        .trim()
                        .toLowerCase()
                        .replace(/\s+/g, '_');
                      if (formattedValue && formattedValue !== categoryValue) {
                        setValue('category', formattedValue);
                      }
                    }
                  }}
                  value={
                    (selectedCategoryOption as any) || {
                      value: categoryValue,
                      label:
                        categoryTranslations[categoryValue] ||
                        categoryValue
                          .toString()
                          .split('_')
                          .map(
                            (word: string) =>
                              word.charAt(0).toUpperCase() + word.slice(1),
                          )
                          .join(' '),
                    }
                  }
                  getOptionLabel={(option) =>
                    (
                      option as {
                        value: string;
                        label: string;
                      }
                    ).label || ''
                  }
                  isOptionEqualToValue={(option, value) =>
                    (
                      option as {
                        value: string;
                        label: string;
                      }
                    ).value ===
                    (value as { value: string; label: string }).value
                  }
                />

                <RHFAutocomplete
                  name="templateType"
                  label="Typ"
                  disabled={
                    isCreateTemplateLoading ||
                    isTemplateCategoriesLoading ||
                    isUpdateTemplateLoading ||
                    isDeleteTemplateLoading
                  }
                  freeSolo
                  options={templateTypeOptions || []}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setValue('templateType', newValue.value);

                      return newValue.value;
                    } else {
                      setValue(
                        'templateType',
                        CreateTemplateDtoTemplateTypeEnum.General,
                      );

                      return '';
                    }
                  }}
                  // onInputChange={(event, newInputValue, reason) => {
                  //   // Handle when user types and blurs without selecting
                  //   if (reason === 'input' && newInputValue) {
                  //     const formattedValue = newInputValue
                  //       .trim()
                  //       .toLowerCase()
                  //       .replace(/\s+/g, '_');
                  //     if (formattedValue && formattedValue !== templateTypeValue) {
                  //       setValue('templateType', newInputValue);
                  //     }
                  //   }
                  // }}
                  value={
                    (selectedTemplateTypeOption as any) || {
                      value: templateTypeValue,
                      label:
                        templateTypeTranslations[templateTypeValue] ||
                        templateTypeValue,
                    }
                  }
                  getOptionLabel={(option) =>
                    (
                      option as {
                        value: string;
                        label: string;
                      }
                    ).label || ''
                  }
                  isOptionEqualToValue={(option, value) =>
                    (
                      option as {
                        value: string;
                        label: string;
                      }
                    ).value ===
                    (value as { value: string; label: string }).value
                  }
                />

                <Box sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}>
                  <Editor
                    disabled={
                      isCreateTemplateLoading ||
                      isTemplateCategoriesLoading ||
                      isUpdateTemplateLoading ||
                      isDeleteTemplateLoading
                    }
                    placeholder={translateKey("hardcoded.tresc_szablonu")}
                    simple
                    id="simple-editor"
                    value={formValues.content}
                    onChange={(_value) => {
                      if (_value.replace(/<(.|\n)*?>/g, '').trim()) {
                        setValue('content', _value);
                      } else {
                        setValue('content', '');
                      }
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <RHFCheckbox
                    name="isShared"
                    label=""
                    disabled={
                      isCreateTemplateLoading ||
                      isTemplateCategoriesLoading ||
                      isUpdateTemplateLoading ||
                      isDeleteTemplateLoading
                    }
                    customLabel={
                      <Typography variant="body2">{translateKey("hardcoded.udostepnij_dla_innych_specjalistow_w_twojej_placowce")}</Typography>
                    }
                    sx={{ mr: 0 }}
                  />
                </Box>
              </Box>
            </Stack>
          </DialogContent>

          <DialogActions
            sx={{ justifyContent: 'space-between', alignItems: 'center' }}
          >
            <LoadingButton
              color="error"
              variant="outlined"
              onClick={handleOpenDeleteModal}
              loading={isDeleteTemplateLoading}
              disabled={
                isCreateTemplateLoading ||
                isTemplateCategoriesLoading ||
                isUpdateTemplateLoading ||
                isDeleteTemplateLoading
              }
            >
              <span>{translateKey("common.remove")}</span>
            </LoadingButton>
            <Stack sx={{ flexDirection: 'row', gap: 1.5 }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleCloseModal}
              >{translateKey("common.cancel")}</Button>
              <LoadingButton
                type="submit"
                variant="contained"
                disabled={
                  isCreateTemplateLoading ||
                  isTemplateCategoriesLoading ||
                  isUpdateTemplateLoading ||
                  isDeleteTemplateLoading
                }
                loading={
                  isCreateTemplateLoading ||
                  isTemplateCategoriesLoading ||
                  isUpdateTemplateLoading
                }
              >
                {isEditing ? translateKey("visit.editAction") : translateKey("common.add")}
              </LoadingButton>
            </Stack>
          </DialogActions>
        </FormProvider>
      </Dialog>
      <TemplatesDeleteModal />
    </>
  );
}
